// PHASE_1 / B2 — time/attendance UX capability derivation (ScopeGate convenience).
//
// PURE helper: derives the time capability strings <ScopeGate> hides actions
// behind from the caller's RESOLVED effective permissions — never from the
// navigation hat (the hat is navigation-only; authority is a permission).
// CONVENIENCE ONLY — the server re-checks every request (ADR-017; the frontend
// is never a trust boundary, ADR-008).
//
// Capability vocabulary (mirrors the route's (module, action) seam):
//   * time.punch     — clock in/out + submit own timesheet (every employee)
//   * time.approve   — approve a submitted timesheet (permission-derived)
//   * time.lock      — lock an approved timesheet (permission-derived)
//   * time.reopen    — reopen a locked timesheet (permission-derived)
//   * time.edit      — edit a time entry (permission-derived)

export const TIME_CAP_PUNCH = "time.punch";
export const TIME_CAP_APPROVE = "time.approve";
export const TIME_CAP_LOCK = "time.lock";
export const TIME_CAP_REOPEN = "time.reopen";
export const TIME_CAP_EDIT = "time.edit";

/**
 * Catalog cells the server gates these transitions on (see
 * services/platform-graph/src/routes/time-attendance.ts): approve AND lock
 * both require matrix `time_attendance.approve` (lock = finalize, an
 * approve-class action — the freeze authority managers hold); reopen and
 * entry-edit require `time_attendance.edit`.
 */
export const TIME_DECIDE_CELL = "time_attendance.approve";
export const TIME_EDIT_CELL = "time_attendance.edit";

/**
 * The time capabilities the caller's resolved permission set exposes in the
 * UI. Self-service punch is UNCONDITIONAL: it is inherent to being an
 * employee (the Personel profile holds no time_attendance write cell — a
 * default-closed matrix derivation would hide the punch button from every
 * employee). Only the manager actions are permission-derived. Returned as a
 * readonly Set for O(1) <ScopeGate> membership tests.
 *
 * @param allowed the caller's resolved `<module>.<action>` capability set
 *   (useEffectivePermissions().capabilities) — empty while loading/on error,
 *   so manager affordances are default-closed until permissions resolve.
 */
export function timeCapabilities(allowed: ReadonlySet<string>): ReadonlySet<string> {
  const caps = new Set<string>([TIME_CAP_PUNCH]);
  if (allowed.has(TIME_DECIDE_CELL)) {
    caps.add(TIME_CAP_APPROVE);
    caps.add(TIME_CAP_LOCK);
  }
  if (allowed.has(TIME_EDIT_CELL)) {
    caps.add(TIME_CAP_REOPEN);
    caps.add(TIME_CAP_EDIT);
  }
  return caps;
}
